import { useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import type { ClientDetail, WireIdentity } from '../wire';

/**
 * Clients — search, detail, edit, archive.
 *
 * The screen that makes the staff/partner asymmetry visible. `internal`
 * preferences are shown here and marked as staff-only, and they exist nowhere on
 * the partner surface: obligation G7, enforced in the database by the
 * `provider_api` role having no `SELECT` on `client_preferences` at all. The
 * badge is not the enforcement — it is the reminder to the person typing that
 * what they write in that box stays behind the counter.
 *
 * Archive rather than delete, for the reason `core-api` gives: `appointments`
 * references clients with `ON DELETE RESTRICT`, and the right to be forgotten is
 * anonymization, which keeps the ledger honest (§7.6 — anonymize, never
 * falsify).
 */
export function Clients(): ReactNode {
  const runtime = useRuntime();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const clients = useLive(() => runtime.mirror.clients(search), [], [search]);
  const detail = useLive(
    async () => (selected === null ? null : ((await runtime.mirror.client(selected)) ?? null)),
    null,
    [selected],
  );

  return (
    <section className="view view--split">
      <div className="pane pane--list">
        <div className="view__header">
          <input
            className="search"
            placeholder="Search clients"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="button button--primary" onClick={() => setAdding(true)}>
            New
          </button>
        </div>

        {clients.length === 0 ? (
          <p className="empty">No one matches.</p>
        ) : (
          <ul className="client-list">
            {clients.map((c) => (
              <li key={c.client_id}>
                <button
                  type="button"
                  className={`client-list__item${selected === c.client_id ? ' is-selected' : ''}`}
                  onClick={() => setSelected(c.client_id)}
                >
                  <strong>{c.display_name}</strong>
                  <small>
                    {c.identities[0]?.identity ?? 'no contact'} · {c.visit_count} visit
                    {c.visit_count === 1 ? '' : 's'}
                  </small>
                  {c.archived ? <span className="badge">archived</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pane pane--detail">
        {detail === null ? (
          <p className="empty">Pick someone.</p>
        ) : (
          <ClientDetailPane client={detail} />
        )}
      </div>

      {adding ? <NewClientSheet onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function ClientDetailPane({ client }: { client: ClientDetail }): ReactNode {
  const actions = useActions();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(client.display_name);
  const [notes, setNotes] = useState(client.notes ?? '');
  const [label, setLabel] = useState('');
  const [internal, setInternal] = useState(false);
  const [category, setCategory] = useState<'preference' | 'allergy'>('preference');

  return (
    <article className="client-detail">
      <header>
        <h1>{client.display_name}</h1>
        <div className="row-actions">
          <button type="button" className="link-button" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Close' : 'Edit'}
          </button>
          <button
            type="button"
            className="link-button link-button--danger"
            onClick={() =>
              void actions.archiveClient({
                clientId: client.client_id,
                name: client.display_name,
                archived: !client.archived,
              })
            }
          >
            {client.archived ? 'Restore' : 'Archive'}
          </button>
        </div>
      </header>

      {editing ? (
        <div className="editor">
          <label className="field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>Notes</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              void actions.updateClient({
                clientId: client.client_id,
                name: client.display_name,
                changes: { name, notes },
              });
              setEditing(false);
            }}
          >
            Save
          </button>
        </div>
      ) : null}

      <h2>Contact</h2>
      <ul className="identity-list">
        {client.identities.map((identity) => (
          <li key={`${identity.identity_kind}:${identity.identity}`}>
            <span>{identity.identity}</span>
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() =>
                void actions.removeIdentity({
                  clientId: client.client_id,
                  name: client.display_name,
                  identity,
                })
              }
            >
              Remove
            </button>
          </li>
        ))}
        {client.identities.length === 0 ? (
          <li className="muted">
            No phone or email — this client cannot be booked until one is added.
          </li>
        ) : null}
      </ul>
      <AddIdentity client={client} />

      <h2>Preferences and allergies</h2>
      <ul className="preference-list">
        {client.preferences.map((p) => (
          <li key={p.preference_id} className={p.internal ? 'is-internal' : ''}>
            <div>
              <strong>{p.label}</strong>
              {p.detail === null ? null : <small>{p.detail}</small>}
            </div>
            <div className="row-actions">
              <span className={`badge badge--${p.category}`}>{p.category}</span>
              {p.internal ? (
                <span className="badge badge--internal" title="Never shown to the booking agent.">
                  staff only
                </span>
              ) : null}
              <button
                type="button"
                className="link-button link-button--danger"
                onClick={() =>
                  void actions.deletePreference({
                    clientId: client.client_id,
                    preferenceId: p.preference_id,
                    name: client.display_name,
                    label: p.label,
                  })
                }
              >
                Remove
              </button>
            </div>
          </li>
        ))}
        {client.preferences.length === 0 ? <li className="muted">Nothing noted yet.</li> : null}
      </ul>

      <div className="editor editor--inline">
        <input
          placeholder="Add a preference or allergy"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value === 'allergy' ? 'allergy' : 'preference')}
        >
          <option value="preference">preference</option>
          <option value="allergy">allergy</option>
        </select>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={internal}
            onChange={(e) => setInternal(e.target.checked)}
          />
          <span>staff only</span>
        </label>
        <button
          type="button"
          className="button"
          disabled={label.trim() === ''}
          onClick={() => {
            void actions.addPreference({
              clientId: client.client_id,
              name: client.display_name,
              category,
              label: label.trim(),
              internal,
            });
            setLabel('');
            setInternal(false);
          }}
        >
          Add
        </button>
      </div>
    </article>
  );
}

function AddIdentity({ client }: { client: ClientDetail }): ReactNode {
  const actions = useActions();
  const [value, setValue] = useState('');
  const [kind, setKind] = useState<WireIdentity['identity_kind']>('phone');

  return (
    <div className="editor editor--inline">
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value === 'email' ? 'email' : 'phone')}
      >
        <option value="phone">phone</option>
        <option value="email">email</option>
      </select>
      <input
        placeholder="Add a phone or email"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button
        type="button"
        className="button"
        disabled={value.trim() === ''}
        onClick={() => {
          void actions.addIdentity({
            clientId: client.client_id,
            name: client.display_name,
            identity: { identity_kind: kind, identity: value.trim() },
          });
          setValue('');
        }}
      >
        Add
      </button>
    </div>
  );
}

function NewClientSheet({ onClose }: { onClose: () => void }): ReactNode {
  const actions = useActions();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  return (
    <div className="sheet" role="dialog" aria-label="New client">
      <div className="sheet__panel">
        <h2>New client</h2>
        <label className="field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
        </label>
        <div className="sheet__actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={name.trim() === ''}
            onClick={() => {
              void actions.createClient({
                name: name.trim(),
                identities:
                  phone.trim() === '' ? [] : [{ identity_kind: 'phone', identity: phone.trim() }],
              });
              onClose();
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
